/**
 * Zutaten für die Anzeige: umgerechnet auf die gewählte Menge und auf Wunsch
 * in Gewicht oder Volumen. Dazu die Platzhalter im Schritttext, zum Beispiel
 * „{mehl} und {zucker} mischen“ → „250 g Mehl und 100 g Zucker mischen“.
 */
import { convertMeasure } from './conversion.ts';
import type { UnitMode } from './conversion.ts';
import { formatQuantity, parseQuantity, scaleQuantity } from './quantity.ts';
import type { DisplayAmount, Quantity } from './quantity.ts';
import type { ScalingMode } from './types.ts';
import { normalizeUnit } from './units.ts';

/** Was von einer Zutat für die Anzeige gebraucht wird. */
export interface IngredientLike {
  readonly amount: string;
  readonly unit: string;
  readonly name: string;
  readonly scaling: ScalingMode;
}

/** Angezeigte Menge einer Zutat. */
export interface PresentedAmount extends DisplayAmount {
  /** Wurde die Angabe in eine andere Einheit umgerechnet? */
  readonly converted: boolean;
}

/** Wendet einen Faktor auf eine Menge an, ohne Umrechnungsart (für Teilmengen). */
function divide(quantity: Quantity, divisor: number): Quantity {
  return {
    value: quantity.value / divisor,
    ...(quantity.max === undefined ? {} : { max: quantity.max / divisor }),
    ...(quantity.approx === true ? { approx: true } : {}),
  };
}

/**
 * Menge einer Zutat für die Anzeige.
 * @param factor Verhältnis der gewünschten zur Originalmenge.
 * @param mode Einheiten wie im Rezept, zum Abwiegen oder zum Abmessen.
 * @param part Teil der Menge, zum Beispiel 2 für „die Hälfte“.
 */
export function presentIngredient(
  ingredient: IngredientLike,
  factor: number,
  mode: UnitMode = 'original',
  part = 1,
): PresentedAmount {
  const quantity = parseQuantity(ingredient.amount);
  const unit = normalizeUnit(ingredient.unit);
  if (quantity === null) {
    return { amount: ingredient.amount.trim(), unit, converted: false };
  }
  // Unverändert lassen, was nicht umgerechnet werden muss: So steht dort
  // genau das, was im Rezept eingetragen wurde.
  if (factor === 1 && mode === 'original' && part === 1) {
    return { amount: ingredient.amount.trim(), unit, converted: false };
  }
  const scaled = divide(scaleQuantity(quantity, factor, ingredient.scaling), part);
  const converted = convertMeasure(scaled.value, unit, mode, ingredient.name);
  if (converted !== null) {
    const ratio = converted.value / scaled.value;
    const target: Quantity = {
      value: converted.value,
      ...(scaled.max === undefined ? {} : { max: scaled.max * ratio }),
      ...(scaled.approx === true ? { approx: true } : {}),
    };
    return { ...formatQuantity(target, converted.unit), converted: converted.unit !== unit };
  }
  return { ...formatQuantity(scaled, unit), converted: false };
}

/** Menge, Einheit und Name als ein Text, zum Beispiel „250 g Mehl“. */
export function ingredientText(ingredient: IngredientLike, shown: DisplayAmount, withName = true): string {
  return [shown.amount, shown.unit, withName ? ingredient.name : ''].filter((part) => part !== '').join(' ');
}

/** Vergleichsform eines Namens: klein, Wörter mit Bindestrich. */
export function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/ß/g, 'ss')
    .replace(/[^a-z0-9äöü]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/**
 * Kurze, eindeutige Platzhalter für alle Zutaten: das erste Wort des Namens,
 * bei Gleichheit so viele Wörter wie nötig, zur Not mit Nummer.
 */
export function placeholderKeys(ingredients: readonly Pick<IngredientLike, 'name'>[]): string[] {
  const words = ingredients.map((ingredient) =>
    slugify(ingredient.name)
      .split('-')
      .filter((word) => word !== ''),
  );
  return words.map((own, index) => {
    for (let length = 1; length <= own.length; length += 1) {
      const key = own.slice(0, length).join('-');
      const clash = words.some(
        (other, otherIndex) => otherIndex !== index && other.slice(0, length).join('-') === key,
      );
      if (!clash) {
        return key;
      }
    }
    const full = own.join('-') || 'zutat';
    const first = words.findIndex((other) => other.join('-') === own.join('-'));
    return first === index ? full : `${full}-${index + 1}`;
  });
}

/** Teil eines Schritttexts nach dem Einsetzen der Platzhalter. */
export type TextPart =
  | { readonly kind: 'text'; readonly text: string }
  | { readonly kind: 'amount'; readonly text: string; readonly ingredient: number }
  | { readonly kind: 'unknown'; readonly text: string };

/** Muster eines Platzhalters: {mehl}, {mehl/2}, {mehl:menge}, {mehl/2:menge}. */
const PLACEHOLDER = /\{([^{}/:]+?)(?:\/(\d+(?:[.,]\d+)?))?(?::(menge))?\}/g;

/** Sucht die Zutat zu einem Platzhalter: erst der genaue Schlüssel, dann ein Namensanfang. */
function findIngredient(
  key: string,
  keys: readonly string[],
  ingredients: readonly IngredientLike[],
): number {
  const wanted = slugify(key);
  const exact = keys.indexOf(wanted);
  if (exact >= 0) {
    return exact;
  }
  return ingredients.findIndex((ingredient) => {
    const slug = slugify(ingredient.name);
    return slug === wanted || slug.startsWith(`${wanted}-`);
  });
}

/**
 * Setzt die Mengen in einen Schritttext ein. Unbekannte Platzhalter bleiben
 * sichtbar stehen, damit sie im Editor auffallen.
 */
export function renderStepText(
  text: string,
  ingredients: readonly IngredientLike[],
  factor = 1,
  mode: UnitMode = 'original',
): TextPart[] {
  const keys = placeholderKeys(ingredients);
  const parts: TextPart[] = [];
  let last = 0;
  for (const match of text.matchAll(PLACEHOLDER)) {
    const start = match.index ?? 0;
    if (start > last) {
      parts.push({ kind: 'text', text: text.slice(last, start) });
    }
    const index = findIngredient(match[1] ?? '', keys, ingredients);
    const ingredient = ingredients[index];
    if (ingredient === undefined) {
      parts.push({ kind: 'unknown', text: match[0] });
    } else {
      const divisor = match[2] === undefined ? 1 : Number(match[2].replace(',', '.')) || 1;
      const shown = presentIngredient(ingredient, factor, mode, divisor);
      const withName = match[3] === undefined;
      const rendered = ingredientText(ingredient, shown, withName);
      parts.push({ kind: 'amount', text: rendered === '' ? ingredient.name : rendered, ingredient: index });
    }
    last = start + match[0].length;
  }
  if (last < text.length) {
    parts.push({ kind: 'text', text: text.slice(last) });
  }
  return parts;
}

/** Schritttext als einfacher Text, zum Beispiel für Vorlesen oder Vorschläge. */
export function stepPlainText(
  text: string,
  ingredients: readonly IngredientLike[],
  factor = 1,
  mode: UnitMode = 'original',
): string {
  return renderStepText(text, ingredients, factor, mode)
    .map((part) => part.text)
    .join('');
}
